import { Component, OnDestroy, OnInit } from '@angular/core';
import { SearchResult } from '../shared/search-result.model';
import { StudySearchResponse } from '../shared/study/model/study-search-response.model';
import { StudySearchRequest } from '../shared/study/model/study-search-request.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { StudyService } from '../shared/study/service/study.service';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { SORT_PREDICATE_NONE } from '../germplasm-manager/germplasm-search-resolve-paging-params';
import { AlertService } from '../shared/alert/alert.service';
import { ColumnFilterComponent, FilterType } from '../shared/column-filter/column-filter.component';
import { MatchType } from '../shared/column-filter/column-filter-text-with-match-options-component';
import { ColumnFilterRadioButtonOption } from '../shared/column-filter/column-filter-radio-component';
import { UrlService } from '../shared/service/url.service';
import { VariableTypeEnum } from '../shared/ontology/variable-type.enum';
import { STUDIES_EDITION_PERMISSIONS } from '../shared/auth/permissions';
import { Principal } from '../shared';
import { ProgramService } from '../shared/program/service/program.service';
import { ParamContext } from '../shared/service/param.context';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { TranslateService } from '@ngx-translate/core';
import { StudyType } from '../shared/study/model/study-type.model';

@Component({
    selector: 'jhi-study-search',
    templateUrl: './study-search.component.html',
    styleUrls: ['./study-search.component.scss']
})
export class StudySearchComponent implements OnInit, OnDestroy {

    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';

    STUDIES_EDITION_PERMISSIONS = [...STUDIES_EDITION_PERMISSIONS];

    itemsPerPage = 20;

    COLUMN_FILTER_EVENT_NAME = StudySearchComponent.COLUMN_FILTER_EVENT_NAME;
    ColumnLabels = ColumnLabels;

    studies: StudySearchResponse[];
    searchRequest: StudySearchRequest;
    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();
    resultSearch: SearchResult;

    currentSearch: string;
    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;

    isLoading: boolean;

    studyFilters: any = [];
    studyTypes: Promise<StudyType[]>;

    user?: any;

    constructor(private jhiLanguageService: JhiLanguageService,
                private studyService: StudyService,
                private router: Router,
                private activatedRoute: ActivatedRoute,
                private alertService: AlertService,
                private eventManager: JhiEventManager,
                private urlService: UrlService,
                private principal: Principal,
                private programService: ProgramService,
                private paramContext: ParamContext,
                private translateService: TranslateService
    ) {
        this.page = 1;
        this.totalItems = 0;
        this.currentSearch = '';
        this.predicate = ColumnLabels.START_DATE;
        this.reverse = false;
        this.resultSearch = new SearchResult('');
        this.searchRequest = new StudySearchRequest();
        this.subscriptionsManager.add = this.activatedRoute.queryParams.subscribe((params) => {
            this.studyTypes =  this.studyService.getStudyTypes(this.paramContext.cropName, this.paramContext.programUUID).toPromise();
        });
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    async ngOnInit() {
        this.user = await this.principal.identity();

        this.filters = this.getInitialFilters(this.getStudyTypes());
        this.registerColumnFiltersChanged();
        this.loadAll(this.request);
    }

    private getStudyTypes() {
        return new Promise((resolve) => {
            this.studyTypes.then((studyTypes: StudyType[]) => {
                resolve(studyTypes.map((studyType) => {
                    return {
                        id: studyType.id,
                        name: 'study-editor.study-type.' + studyType.label.toLowerCase().trim().replace(/ /g, '.')
                    };
                }));
            });
        });
    }

    get request() {
        return this.searchRequest;
    }

    set request(request: StudySearchRequest) {
        this.searchRequest = request;
    }

    get filters() {
        return this.studyFilters;
    }

    set filters(filters) {
        this.studyFilters = filters;
    }

    loadAll(request: StudySearchRequest) {
        this.isLoading = true;
        this.studyService.searchStudies(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<StudySearchResponse[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                search: this.currentSearch,
                sort: this.getSort()
            },
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    trackId(index: number, item: StudySearchResponse) {
        return item.studyId;
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    openViewSummary($event, study: StudySearchResponse) {
        $event.preventDefault();
        this.router.navigate([`/study-manager/study/${study.studyId}`], {
            queryParams: {
                studyId: study.studyId,
                studyName: study.studyName
            },
            queryParamsHandling: 'merge'
        });
    }

    async openStudyNew(study: StudySearchResponse) {

        if (this.urlService.isStudyNotUserAccessible(study.studyId, study.studyName, study.locked, study.ownerId, this.user)) {
            this.alertService.error('study.manager.errors.study-locked', { param: study.ownerName });
            return;
        }

        const program = await this.programService.getProgramByProgramUUID(this.paramContext.cropName, study.program_uuid).toPromise();
        if (program && program.body) {
            this.urlService.openStudy(study.studyId, study.studyName, program.body);
        }
    }


    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(StudySearchComponent.COLUMN_FILTER_EVENT_NAME, (event) => {
            this.resetTable();
        });
    }

    private getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    private clearSort() {
        this.predicate = SORT_PREDICATE_NONE;
        this.reverse = false;
    }

    private onSuccess(data: StudySearchResponse[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.studies = data;
    }

    private getInitialFilters(studyTypes) {
        return [
            {
                key: 'studyNameFilter', name: 'column-filter.study.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            { key: 'withMeansData', name: 'column-filter.with.means.data.only', type: FilterType.BOOLEAN, value: false, default: true },
            {
                key: 'studyTypeIds', name: 'column-filter.study.type', type: FilterType.CHECKLIST,
                value: undefined,
                translate: true,
                options: studyTypes
            },
            { key: 'locked', name: 'column-filter.locked', type: FilterType.RADIOBUTTON, options: this.getStatusFilterOptions() },
            { key: 'ownerName', name: 'column-filter.created.by', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'studyDate', name: 'column-filter.date', type: FilterType.DATE,
                fromKey: 'studyStartDateFrom',
                toKey: 'studyStartDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            { key: 'parentFolderName', name: 'column-filter.parent.folder', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'objective', name: 'column-filter.objective', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'studySettings', name: 'column-filter.study.settings', type: FilterType.VARIABLES, placeholder: 'column-filter.study.settings.placeholder',
                description: 'column-filter.study.settings.label', variableTypeIds: [VariableTypeEnum.STUDY_DETAIL.toString()], variables: [],
                transform(req) {
                    ColumnFilterComponent.transformVariablesFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetVariablesFilter(this, req);
                },
            },
            {
                key: 'environmentDetails', name: 'column-filter.environment.details', type: FilterType.VARIABLES, placeholder: 'column-filter.environment.details.placeholder',
                description: 'column-filter.environment.details.label', variableTypeIds: [VariableTypeEnum.ENVIRONMENT_DETAIL.toString()], variables: [],
                transform(req) {
                    ColumnFilterComponent.transformVariablesFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetVariablesFilter(this, req);
                },
            },
            {
                key: 'environmentConditions', name: 'column-filter.environment.conditions', type: FilterType.VARIABLES, placeholder: 'column-filter.environment.conditions.placeholder',
                description: 'column-filter.environment.conditions.label', variableTypeIds: [VariableTypeEnum.ENVIRONMENT_CONDITION.toString()], variables: [],
                transform(req) {
                    ColumnFilterComponent.transformVariablesFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetVariablesFilter(this, req);
                },
            }];
    }

    private getStatusFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([new ColumnFilterRadioButtonOption(true, 'common.form.yes.text'),
                new ColumnFilterRadioButtonOption(false, 'common.form.no.text')]);
        });
    }

    getStudyType(studyType: string) {
        if (studyType) {
            return 'study-editor.study-type.' + studyType.toLowerCase().trim().replace(/ /g, '.');
        }
        return '';
    }

}

export enum ColumnLabels {
    'STUDY_NAME' = 'STUDY_NAME',
    'STUDY_TYPE_NAME' = 'STUDY_TYPE_NAME',
    'LOCKED' = 'LOCKED',
    'STUDY_OWNER_NAME' = 'STUDY_OWNER_NAME',
    'START_DATE' = 'START_DATE',
    'PARENT_FOLDER_NAME' = 'PARENT_FOLDER_NAME',
    'OBJECTIVE' = 'OBJECTIVE'
}
